import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './VereinsDashboard.css';

const fmt = (d) =>
  d ? new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' }) : '–';

/**
 * Betrag als Euro, oder ein Strich.
 *
 * ⚠️ `null` heisst „keine Gebuehr hinterlegt" und wird als „–" gezeigt, NICHT
 * als 0,00 €. Der Unterschied ist wichtig: eine Vormerkung ohne Kategorie hat
 * noch keinen Start und damit keine Gebuehr; 0,00 € waere die Aussage „kostet
 * nichts".
 */
const euro = (betrag) => {
  const n = parseFloat(betrag);
  return Number.isFinite(n)
    ? n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })
    : '–';
};

/** Summe der Starts, die nicht storniert sind. */
const summeOffen = (zeilen = []) =>
  zeilen
    .filter((r) => r.status !== 'Storniert')
    .reduce((s, r) => s + (parseFloat(r.startgebuehr) || 0), 0);

const GURTFARBEN = [
  'Weiß', 'Weiß-Gelb', 'Gelb', 'Gelb-Grün', 'Orange', 'Grün',
  'Grün-Blau', 'Blau', 'Blau-Rot', 'Rot', 'Rot-Braun', 'Braun', 'Schwarz',
];

const SKILL_LEVELS = ['Beginner', 'Intermediate', 'Advanced', 'Black Belt'];

const gurtToSkill = (gurt) => {
  if (!gurt) return 'Beginner';
  const g = gurt.toLowerCase();
  if (g.includes('schwarz')) return 'Black Belt';
  if (g.includes('rot') || g.includes('braun')) return 'Advanced';
  if (g.includes('grün') || g.includes('gruen') || g.includes('blau')) return 'Intermediate';
  return 'Beginner';
};

const SKILL_BADGE = {
  'Beginner':    'muted',
  'Intermediate':'blue',
  'Advanced':    'gold',
  'Black Belt':  'black',
};

export default function VereinsDashboard() {
  const navigate = useNavigate();

  // Auth
  const stored = JSON.parse(localStorage.getItem('clubData') || '{}');
  const vereinsId = stored?.user?.vereins_id;
  const vereinName = stored?.user?.name || 'Mein Verein';
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // State
  const [tab, setTab] = useState('übersicht');
  const [profile, setProfile] = useState(null);
  const [athletes, setAthletes] = useState([]);
  const [anmeldungen, setAnmeldungen] = useState([]);
  const [turniere, setTurniere] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ text: '', type: '' });

  // Profil-Bearbeitung
  const [editProfile, setEditProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({});

  // Wettkämpfer-Modal
  const [wkModal, setWkModal] = useState(null); // null | { type: 'add'|'edit' }
  const [wkForm, setWkForm] = useState({});

  // Turnier-Anmeldungs-Modal
  const [turnierModal, setTurnierModal] = useState(null); // null | { turnier }
  const [selectedAthletes, setSelectedAthletes] = useState(new Set());

  // Übersicht Sub-Tab
  const [overviewTab, setOverviewTab] = useState('dashboard');

  // Suche
  const [wkSearch, setWkSearch] = useState('');

  // Dojosoftware-Integration
  const [integration, setIntegration] = useState(null);  // null = nicht geladen
  const [integForm, setIntegForm] = useState({ dojo_api_url: '', api_token: '' });
  const [integLoading, setIntegLoading] = useState(false);
  const [integTestResult, setIntegTestResult] = useState(null); // null | { ok, msg }

  // Auth-Check
  useEffect(() => {
    if (!vereinsId) {
      navigate('/login', { replace: true });
      return;
    }
    loadAll();
    loadIntegration();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const showMsg = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 3500);
  };

  const loadAll = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [profRes, athRes, anmRes, turnRes] = await Promise.all([
        fetch(`/api/vereine/${vereinsId}`, { headers }),
        fetch(`/api/wettkaempfer/verein/${vereinsId}`, { headers }),
        fetch(`/api/anmeldungen/verein/${vereinsId}`, { headers }),
        fetch('/api/turniere/public'),
      ]);

      const [pData, aData, anmData, tData] = await Promise.all([
        profRes.json(), athRes.json(), anmRes.json(), turnRes.json(),
      ]);

      // Profil
      const p = pData.data || pData;
      if (p && (p.vereins_id || p.name)) {
        setProfile(p);
        setProfileForm({
          name: p.name || '', email: p.email || '',
          ansprechpartner: p.ansprechpartner || '', telefon: p.telefon || '',
          homepage: p.homepage || '',
          strasse: p.strasse || '', hausnummer: p.hausnummer || '',
          plz: p.plz || '', ort: p.ort || '',
        });
      }

      // Wettkämpfer
      const aList = aData.success ? (aData.data || []) : (Array.isArray(aData) ? aData : []);
      setAthletes(aList);

      // Anmeldungen — API returns { success, data: { anmeldungen: [...] } }
      const rawAnm = anmData?.data?.anmeldungen ?? anmData?.data ?? anmData;
      setAnmeldungen(Array.isArray(rawAnm) ? rawAnm : []);

      // Turniere (public)
      const tList = tData.success
        ? (tData.data || [])
        : (Array.isArray(tData) ? tData : []);
      setTurniere(tList);

    } catch (e) {
      console.error('Ladefehler:', e);
      showMsg('Fehler beim Laden der Daten.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Dojosoftware-Integration laden
  const loadIntegration = async () => {
    try {
      const r = await fetch(`/api/dojo-sync/integration/${vereinsId}`, { headers });
      const d = await r.json();
      if (d.success) {
        setIntegration(d.integration);
        if (d.integration) {
          setIntegForm({
            dojo_api_url: d.integration.dojo_api_url || '',
            api_token: '',
          });
        }
      }
    } catch { /* ignorieren */ }
  };

  const testConnection = async () => {
    if (!integForm.dojo_api_url || !integForm.api_token) {
      setIntegTestResult({ ok: false, msg: 'Bitte API-URL und API-Token eingeben.' });
      return;
    }
    setIntegLoading(true);
    setIntegTestResult(null);
    try {
      const r = await fetch('/api/dojo-sync/check-connection', {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ dojo_api_url: integForm.dojo_api_url, api_token: integForm.api_token }),
      });
      const d = await r.json();
      if (d.success) {
        const dojo = d.dojos?.[0] || d.authenticated_dojo;
        setIntegTestResult({ ok: true, msg: `Verbindung erfolgreich! Dojo: ${dojo?.name || '–'} (${dojo?.mitglieder_count ?? '?'} Mitglieder)` });
      } else {
        setIntegTestResult({ ok: false, msg: d.message || 'Verbindung fehlgeschlagen.' });
      }
    } catch (e) {
      setIntegTestResult({ ok: false, msg: 'Verbindungsfehler: ' + e.message });
    } finally {
      setIntegLoading(false);
    }
  };

  const saveIntegration = async () => {
    if (!integForm.dojo_api_url || !integForm.api_token) {
      showMsg('Bitte API-URL und API-Token eingeben.', 'error');
      return;
    }
    setIntegLoading(true);
    try {
      const r = await fetch(`/api/dojo-sync/integration/${vereinsId}`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(integForm),
      });
      const d = await r.json();
      if (d.success || r.ok) {
        await loadIntegration();
        setIntegForm(f => ({ ...f, api_token: '' }));
        showMsg('Integration gespeichert.');
      } else {
        showMsg(d.message || 'Fehler beim Speichern.', 'error');
      }
    } catch { showMsg('Verbindungsfehler.', 'error'); }
    finally { setIntegLoading(false); }
  };

  const syncNow = async () => {
    setIntegLoading(true);
    try {
      const r = await fetch('/api/dojo-sync/sync-verein', {
        method: 'POST',
        headers,
      });
      const d = await r.json();
      if (d.success) {
        await loadAll(true);
        await loadIntegration();
        showMsg(`Sync abgeschlossen: ${d.imported} neu, ${d.updated} aktualisiert${d.errors ? `, ${d.errors} Fehler` : ''}.`);
      } else {
        showMsg(d.message || 'Sync fehlgeschlagen.', 'error');
      }
    } catch { showMsg('Verbindungsfehler.', 'error'); }
    finally { setIntegLoading(false); }
  };

  // Logout
  const handleLogout = () => {
    ['token', 'authToken', 'clubData', 'clubId', 'adminData', 'vereins_id']
      .forEach(k => localStorage.removeItem(k));
    navigate('/login', { replace: true });
  };

  // Profil speichern
  const saveProfile = async () => {
    try {
      const r = await fetch(`/api/vereine/${vereinsId}`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(profileForm),
      });
      const d = await r.json();
      if (d.success || r.ok) {
        setProfile({ ...profile, ...profileForm });
        setEditProfile(false);
        showMsg('Profil gespeichert.');
      } else {
        showMsg(d.error || d.message || 'Fehler beim Speichern.', 'error');
      }
    } catch { showMsg('Verbindungsfehler.', 'error'); }
  };

  // Wettkämpfer speichern (add/edit)
  const saveWk = async () => {
    const isEdit = wkModal?.type === 'edit';
    const id = wkForm.wettkaempfer_id || wkForm.id;
    const url = isEdit ? `/api/wettkaempfer/${id}` : '/api/wettkaempfer';
    try {
      const r = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...wkForm, vereins_id: vereinsId }),
      });
      const d = await r.json();
      if (d.success || r.ok) {
        setWkModal(null);
        await loadAll(true);
        showMsg(isEdit ? 'Wettkämpfer aktualisiert.' : 'Wettkämpfer hinzugefügt.');
      } else {
        // ⚠️ Der eigentliche Grund steht in `details`, nicht in `error`.
        // `error` ist nur „Validierungsfehler" -- damit weiss niemand, welches
        // Feld gemeint ist. Gemeldet wird, was der Server wirklich beanstandet.
        const grund = Array.isArray(d.details) && d.details.length > 0
          ? d.details.join(' · ')
          : (d.error || d.message || 'Fehler.');
        showMsg(grund, 'error');
      }
    } catch { showMsg('Verbindungsfehler.', 'error'); }
  };

  // Wettkämpfer löschen
  const deleteWk = async (wk) => {
    if (!window.confirm(`${wk.vorname} ${wk.nachname} wirklich löschen?`)) return;
    const id = wk.wettkaempfer_id || wk.id;
    try {
      const r = await fetch(`/api/wettkaempfer/${id}`, { method: 'DELETE', headers });
      if (r.ok) {
        await loadAll(true);
        showMsg('Wettkämpfer gelöscht.');
      } else {
        const d = await r.json().catch(() => ({}));
        showMsg(d.error || 'Fehler beim Löschen.', 'error');
      }
    } catch { showMsg('Verbindungsfehler.', 'error'); }
  };

  // Turnier-Anmeldungs-Modal öffnen
  const openTurnierModal = (turnier) => {
    const tid = turnier.turnier_id || turnier.id;
    const already = new Set(
      anmeldungen
        .filter(a => a.turnier_id === tid && a.status !== 'Storniert')
        .map(a => a.wettkampfer_id || a.wettkaempfer_id)
    );
    setSelectedAthletes(already);
    setTurnierModal({ turnier });
  };

  const toggleAthlete = (id) => {
    setSelectedAthletes(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const submitTurnierAnmeldung = async () => {
    if (!turnierModal || selectedAthletes.size === 0) return;
    const turnierId = turnierModal.turnier.turnier_id || turnierModal.turnier.id;
    try {
      const r = await fetch(`/api/turniere/${turnierId}/register`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ wettkampfer_ids: [...selectedAthletes] }),
      });
      const d = await r.json();
      if (d.success || r.ok) {
        setTurnierModal(null);
        await loadAll(true);
        showMsg(`${selectedAthletes.size} Wettkämpfer vorgemerkt — Kategorien fehlen noch.`);
      } else {
        showMsg(d.error || d.message || 'Fehler bei der Anmeldung.', 'error');
      }
    } catch { showMsg('Verbindungsfehler.', 'error'); }
  };

  // Anmeldung stornieren
  //
  // ⚠️ Der Server verlangt einen Stornierungsgrund (mindestens 3 Zeichen) --
  // hier ging der DELETE bis zum 16.09.2026 OHNE RUMPF raus. Er antwortete
  // deshalb immer mit 400, und Stornieren war ueber das Vereins-Dashboard
  // ueberhaupt nicht moeglich. Der Grund wird jetzt erfragt und mitgeschickt.
  const storniereAnmeldung = async (anm) => {
    const turnierId = anm.turnier_id;
    const anmId = anm.anmeldung_id || anm.id;
    const wer = `${anm.vorname} ${anm.nachname}`;
    const kategorie = anm.division_code ? ` (${anm.division_code})` : '';
    const grund = window.prompt(
      `Anmeldung von ${wer}${kategorie} stornieren.\n\nWarum? Der Grund wird beim Start vermerkt.`,
      ''
    );
    // Abbrechen im Fenster liefert null -- dann passiert nichts.
    if (grund === null) return;
    if (grund.trim().length < 3) {
      showMsg('Bitte einen Grund mit mindestens 3 Zeichen angeben.', 'error');
      return;
    }
    try {
      const r = await fetch(`/api/turniere/${turnierId}/registrations/${anmId}`, {
        method: 'DELETE',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ grund: grund.trim() }),
      });
      if (r.ok) {
        await loadAll(true);
        showMsg('Anmeldung storniert.');
      } else {
        const d = await r.json().catch(() => ({}));
        showMsg(d.error || 'Fehler beim Stornieren.', 'error');
      }
    } catch { showMsg('Verbindungsfehler.', 'error'); }
  };

  // Computed
  const filteredAthletes = athletes.filter(a =>
    `${a.vorname} ${a.nachname}`.toLowerCase().includes(wkSearch.toLowerCase())
  );

  const safeAnmeldungen = Array.isArray(anmeldungen) ? anmeldungen : [];
  const byTurnier = safeAnmeldungen.reduce((map, a) => {
    const k = a.turnier_id;
    if (!map[k]) map[k] = { id: k, name: a.turnier_name, datum: a.turnier_datum, rows: [] };
    map[k].rows.push(a);
    return map;
  }, {});
  const turnierGroups = Object.values(byTurnier).sort((a, b) => new Date(b.datum) - new Date(a.datum));

  const activeStarts = safeAnmeldungen.filter(a => a.status === 'Angemeldet' || a.status === 'Bestätigt').length;

  const statusBadge = (s) => {
    if (s === 'Storniert') return 'danger';
    if (s === 'Bestätigt') return 'success';
    return 'warning';
  };

  // ── Loading State ──────────────────────────────────────────────────────────
  if (loading) return (
    <div className="vd-loading">
      <div className="vd-loading-spinner" />
      <p>Lade Vereinsdashboard…</p>
    </div>
  );

  return (
    <div className="vd-page">

      {/* ── Topbar ─────────────────────────────────────────────────────────── */}
      <nav className="vd-topbar">
        <div className="vd-topbar-left">
          <img src="/logo.webp" alt="TDA" className="vd-topbar-logo" />
          <div>
            <span className="vd-topbar-title">TDA Tournament Center</span>
            <span className="vd-topbar-sub"> — {profile?.name || vereinName}</span>
          </div>
        </div>
        <div className="vd-topbar-right">
          <span className="vd-topbar-badge">Verein</span>
          <button className="vd-btn vd-btn--ghost vd-btn--sm" onClick={handleLogout}>
            Abmelden
          </button>
        </div>
      </nav>

      {/* ── Message ────────────────────────────────────────────────────────── */}
      {message.text && (
        <div className={`vd-message vd-message--${message.type}`}>
          {message.text}
        </div>
      )}

      {/* ── Tabs ───────────────────────────────────────────────────────────── */}
      <div className="vd-tabs-bar">
        {[
          ['übersicht',      '📊 Übersicht'],
          ['wettkämpfer',    '🥋 Wettkämpfer'],
          ['veranstaltungen','🏆 Veranstaltungen'],
          ['anmeldungen',    '📋 Anmeldungen'],
          ['profil',         '⚙️ Profil'],
          ['integration',    '🔗 Dojosoftware'],
        ].map(([id, label]) => (
          <button
            key={id}
            className={`vd-tab ${tab === id ? 'active' : ''}`}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="vd-content">

        {/* ══════════════════════════════════════════════════════════════════
            ÜBERSICHT
        ══════════════════════════════════════════════════════════════════ */}
        {tab === 'übersicht' && (
          <div>
            {/* ── Sub-Tabs ── */}
            <div className="vd-sub-tabs">
              <button
                className={`vd-sub-tab ${overviewTab === 'dashboard' ? 'active' : ''}`}
                onClick={() => setOverviewTab('dashboard')}
              >
                Dashboard
              </button>
              <button
                className={`vd-sub-tab ${overviewTab === 'events' ? 'active' : ''}`}
                onClick={() => setOverviewTab('events')}
              >
                Kommende Veranstaltungen
                {turniere.length > 0 && (
                  <span className="vd-sub-tab-count">{turniere.length}</span>
                )}
              </button>
            </div>

            {/* ── Dashboard ── */}
            {overviewTab === 'dashboard' && (
              <>
                <div className="vd-stats-row">
                  <div className="vd-stat-card vd-stat-card--blue">
                    <div className="vd-stat-value">{athletes.length}</div>
                    <div className="vd-stat-label">Wettkämpfer</div>
                  </div>
                  <div className="vd-stat-card vd-stat-card--green">
                    <div className="vd-stat-value">{turnierGroups.length}</div>
                    <div className="vd-stat-label">Turniere mit Starts</div>
                  </div>
                  <div className="vd-stat-card vd-stat-card--gold">
                    <div className="vd-stat-value">{safeAnmeldungen.length}</div>
                    <div className="vd-stat-label">Starts gesamt</div>
                  </div>
                  <div className="vd-stat-card vd-stat-card--red">
                    <div className="vd-stat-value">{activeStarts}</div>
                    <div className="vd-stat-label">Aktive Starts</div>
                  </div>
                </div>

                <div className="vd-row">
                  <div className="vd-card">
                    <div className="vd-card-header"><h3>Letzte Anmeldungen</h3></div>
                    {turnierGroups.length === 0 ? (
                      <p className="vd-empty">Noch keine Anmeldungen.</p>
                    ) : turnierGroups.slice(0, 5).map(t => (
                      <div key={t.id} className="vd-list-item">
                        <div>
                          <div className="vd-list-item-title">{t.name}</div>
                          <div className="vd-list-item-sub">{fmt(t.datum)} · {t.rows.length} Starts</div>
                        </div>
                        <span className="vd-badge vd-badge--success">
                          {t.rows.filter(r => r.status !== 'Storniert').length} aktiv
                        </span>
                      </div>
                    ))}
                    {turnierGroups.length > 5 && (
                      <button className="vd-link-btn" onClick={() => setTab('anmeldungen')}>
                        Alle {turnierGroups.length} Turniere anzeigen →
                      </button>
                    )}
                  </div>

                  <div className="vd-card">
                    <div className="vd-card-header"><h3>Nächste Veranstaltungen</h3></div>
                    {turniere.length === 0 ? (
                      <p className="vd-empty">Keine bevorstehenden Veranstaltungen.</p>
                    ) : turniere.slice(0, 4).map(t => (
                      <div key={t.turnier_id || t.id} className="vd-list-item">
                        <div>
                          <div className="vd-list-item-title">{t.name}</div>
                          <div className="vd-list-item-sub">{fmt(t.datum)} · {t.ort || '–'}</div>
                        </div>
                        <button
                          className="vd-btn vd-btn--gold vd-btn--sm"
                          onClick={() => setOverviewTab('events')}
                        >
                          Details
                        </button>
                      </div>
                    ))}
                    {turniere.length > 4 && (
                      <button className="vd-link-btn" onClick={() => setOverviewTab('events')}>
                        Alle {turniere.length} Veranstaltungen anzeigen →
                      </button>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* ── Kommende Veranstaltungen ── */}
            {overviewTab === 'events' && (
              <div>
                {turniere.length === 0 ? (
                  <div className="vd-empty-state">
                    <p>Aktuell sind keine Veranstaltungen ausgeschrieben.</p>
                  </div>
                ) : turniere.map(t => {
                  const tid = t.turnier_id || t.id;
                  const eigene = safeAnmeldungen.filter(a => a.turnier_id === tid && a.status !== 'Storniert').length;
                  const deadline = t.anmeldeschluss ? new Date(t.anmeldeschluss) : null;
                  const deadlinePassed = deadline && deadline < new Date();
                  return (
                    <div key={tid} className="vd-card vd-event-card">
                      <div className="vd-turnier-header">
                        <div className="vd-event-info">
                          <div className="vd-turnier-name">{t.name}</div>
                          <div className="vd-turnier-meta">
                            <span>📅 {fmt(t.datum)}</span>
                            {t.ort && <span>📍 {t.ort}</span>}
                            {t.disziplin && <span>🥋 {t.disziplin}</span>}
                          </div>
                          {deadline && (
                            <div className={`vd-deadline ${deadlinePassed ? 'vd-deadline--passed' : ''}`}>
                              ⏰ Anmeldeschluss: {fmt(t.anmeldeschluss)}
                              {deadlinePassed ? ' — abgelaufen' : ''}
                            </div>
                          )}
                          {t.beschreibung && (
                            <div className="vd-event-desc">{t.beschreibung}</div>
                          )}
                        </div>
                        <div className="vd-turnier-actions">
                          {eigene > 0 && (
                            <span className="vd-badge vd-badge--success">{eigene} angemeldet</span>
                          )}
                          <button
                            className="vd-btn vd-btn--gold"
                            onClick={() => { setTab('veranstaltungen'); }}
                            disabled={deadlinePassed && eigene === 0}
                          >
                            {eigene > 0 ? 'Anmeldung ansehen' : deadlinePassed ? 'Abgelaufen' : 'Anmelden'}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            WETTKÄMPFER
        ══════════════════════════════════════════════════════════════════ */}
        {tab === 'wettkämpfer' && (
          <div className="vd-card">
            <div className="vd-card-header">
              <h3>{athletes.length} Wettkämpfer</h3>
              <div className="vd-card-actions">
                <input
                  className="vd-search"
                  placeholder="Suchen…"
                  value={wkSearch}
                  onChange={e => setWkSearch(e.target.value)}
                />
                <button
                  className="vd-btn vd-btn--gold"
                  onClick={() => {
                    setWkForm({ geschlecht: 'männlich', gurtfarbe: 'Weiß', skill_level: 'Beginner' });
                    setWkModal({ type: 'add' });
                  }}
                >
                  + Hinzufügen
                </button>
              </div>
            </div>

            {/* ── Wer hat kein Gewicht? ────────────────────────────────────
                Seit dem 16.09.2026 ist das Gewicht beim Anlegen Pflicht. Das
                loest aber den BESTAND nicht: Wettkaempfer, die aus der
                Dojosoftware uebernommen wurden, kommen ohne Gewicht an (dort
                wird keines gefuehrt), und der Abgleich darf daran nicht
                scheitern -- sonst kommen gar keine Mitglieder mehr an.
                Gemessen am 16.09.: 81 von 81 uebernommenen ohne Gewicht,
                gegenueber 1 von 374 selbst angelegten.
                Deshalb steht die Luecke hier, statt still zu bleiben. */}
            {athletes.filter(a => !(parseFloat(a.gewicht) > 0)).length > 0 && (
              <div className="vd-gewicht-fehlt" role="status">
                <span aria-hidden="true">⚖️</span>
                <div>
                  <strong>
                    {athletes.filter(a => !(parseFloat(a.gewicht) > 0)).length} Wettkämpfer ohne Gewicht
                  </strong>
                  <div className="vd-gewicht-fehlt-zeile">
                    Für sie lässt sich keine Gewichtsklasse zuordnen — bei der Anmeldung stehen
                    dann alle Klassen zur Wahl, und bei der Einwaage muss die Gruppe neu gebaut
                    werden. In der Liste unten mit „⚖️ fehlt" markiert.
                  </div>
                </div>
              </div>
            )}

            {filteredAthletes.length === 0 ? (
              <p className="vd-empty">
                {wkSearch ? 'Keine Treffer.' : 'Noch keine Wettkämpfer erfasst.'}
              </p>
            ) : (
              <table className="vd-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Geburtsdatum</th>
                    <th>Geschlecht</th>
                    <th>Gewicht</th>
                    <th>Gurtfarbe</th>
                    <th>Skill Level</th>
                    <th>Starts</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAthletes.map(a => {
                    const wkId = a.wettkaempfer_id || a.id;
                    const starts = anmeldungen.filter(r =>
                      (r.wettkampfer_id || r.wettkaempfer_id) === wkId && r.status !== 'Storniert'
                    ).length;
                    return (
                      <tr key={wkId}>
                        <td className="vd-td-name">{a.nachname}, {a.vorname}</td>
                        <td>{fmt(a.geburtsdatum)}</td>
                        <td>{a.geschlecht || '–'}</td>
                        <td>
                          {parseFloat(a.gewicht) > 0
                            ? `${a.gewicht} kg`
                            : <span className="vd-fehlt" title="Ohne Gewicht lässt sich keine Gewichtsklasse zuordnen.">⚖️ fehlt</span>}
                        </td>
                        <td>{a.gurtfarbe || '–'}</td>
                        <td>
                          {a.skill_level
                            ? <span className={`vd-badge vd-badge--${SKILL_BADGE[a.skill_level] || 'warning'}`}>{a.skill_level}</span>
                            : '–'}
                        </td>
                        <td className="vd-td-gold">{starts}</td>
                        <td>
                          <div className="vd-row-actions">
                            <button
                              className="vd-btn vd-btn--ghost vd-btn--sm"
                              onClick={() => { setWkForm({ ...a }); setWkModal({ type: 'edit' }); }}
                            >
                              Bearbeiten
                            </button>
                            <button
                              className="vd-btn vd-btn--danger vd-btn--sm"
                              onClick={() => deleteWk(a)}
                            >
                              Löschen
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            VERANSTALTUNGEN
        ══════════════════════════════════════════════════════════════════ */}
        {tab === 'veranstaltungen' && (
          <div>
            {turniere.length === 0 ? (
              <div className="vd-empty-state">
                <p>Aktuell sind keine Veranstaltungen ausgeschrieben.</p>
              </div>
            ) : turniere.map(t => {
              const tid = t.turnier_id || t.id;
              const eigene = anmeldungen.filter(a => a.turnier_id === tid && a.status !== 'Storniert').length;
              return (
                <div key={tid} className="vd-card">
                  <div className="vd-turnier-header">
                    <div>
                      <div className="vd-turnier-name">{t.name}</div>
                      <div className="vd-turnier-meta">
                        {/* ⚠️ Ein blosser Strich sagt nicht, was Sache ist.
                            „Termin noch offen" beantwortet die Frage, die der
                            Strich aufwirft — und erklaert, warum nur
                            vorgemerkt werden kann. */}
                        <span>📅 {t.datum ? fmt(t.datum) : 'Termin noch offen'}</span>
                        {t.ort && <span>📍 {t.ort}</span>}
                        {t.disziplin && <span>🥋 {t.disziplin}</span>}
                        {t.anmeldeschluss && <span>⏰ Anmeldeschluss: {fmt(t.anmeldeschluss)}</span>}
                      </div>
                      {athletes.length === 0 && (
                        <p className="vd-hint">
                          Keine Wettkämpfer vorhanden. Bitte zuerst Wettkämpfer hinzufügen.
                        </p>
                      )}
                    </div>
                    <div className="vd-turnier-actions">
                      {eigene > 0 && (
                        <span className="vd-badge vd-badge--success">{eigene} angemeldet</span>
                      )}
                      <button
                        className="vd-btn vd-btn--gold"
                        onClick={() => openTurnierModal(t)}
                        disabled={athletes.length === 0}
                      >
                        {/* ⚠️ Ohne Datum ist das Turnier nur vormerkbar
                            (Entscheidung 16.09.2026). „Anmelden" waere das
                            falsche Wort -- es weckt die Erwartung, dass man
                            Kategorien waehlen kann. */}
                        {eigene > 0
                          ? 'Anmeldung bearbeiten'
                          : (t.datum ? 'Anmelden' : 'Vormerken')}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            ANMELDUNGEN
        ══════════════════════════════════════════════════════════════════ */}
        {tab === 'anmeldungen' && (
          <div>
            {turnierGroups.length === 0 ? (
              <div className="vd-empty-state">
                <p>Noch keine Anmeldungen vorhanden.</p>
                <button className="vd-btn vd-btn--gold" onClick={() => setTab('veranstaltungen')}>
                  Zu Veranstaltungen
                </button>
              </div>
            ) : turnierGroups.map(t => (
              <div key={t.id} className="vd-card">
                <div className="vd-card-header">
                  <div>
                    <h3>{t.name}</h3>
                    <span className="vd-sub">{fmt(t.datum)}</span>
                  </div>
                  <span className="vd-badge vd-badge--gold">{t.rows.length} Starts</span>
                </div>
                {/* ⚠️ Die Liste zeigte keinen einzigen Betrag. Der Verein
                    konnte nicht sehen, was eine Anmeldung kostet -- und
                    schon gar nicht, dass die zweite und jede weitere
                    Kategorie guenstiger ist. Gezeigt wird die TATSAECHLICHE
                    Gebuehr der Anmeldung, nicht der Turnierpreis. */}
                <table className="vd-table">
                  <thead>
                    <tr>
                      <th>Wettkämpfer</th>
                      <th>Kategorie</th>
                      <th>Angemeldet am</th>
                      <th>Status</th>
                      <th className="vd-td-geld">Startgebühr</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {t.rows.map((r, i) => (
                      <tr key={i}>
                        <td className="vd-td-name">{r.nachname}, {r.vorname}</td>
                        <td>{r.division_code || r.kategorie || '–'}</td>
                        <td>{fmt(r.anmeldedatum || r.erstellt_am)}</td>
                        <td>
                          <span className={`vd-badge vd-badge--${statusBadge(r.status)}`}>
                            {r.status}
                          </span>
                        </td>
                        <td className="vd-td-geld">
                          {euro(r.startgebuehr)}
                          {r.bezahlt ? <span className="vd-bezahlt" title="bezahlt"> ✓</span> : null}
                        </td>
                        <td>
                          {r.status !== 'Storniert' && (
                            <button
                              className="vd-btn vd-btn--danger vd-btn--sm"
                              onClick={() => storniereAnmeldung(r)}
                            >
                              Stornieren
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={4} className="vd-summe-label">
                        Summe der offenen Starts
                      </td>
                      <td className="vd-td-geld vd-summe">{euro(summeOffen(t.rows))}</td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            ))}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            PROFIL
        ══════════════════════════════════════════════════════════════════ */}
        {tab === 'profil' && (
          <div className="vd-card">
            <div className="vd-card-header">
              <h3>Vereinsprofil</h3>
              {!editProfile ? (
                <button className="vd-btn vd-btn--gold" onClick={() => setEditProfile(true)}>
                  Bearbeiten
                </button>
              ) : (
                <div className="vd-card-actions">
                  <button className="vd-btn vd-btn--ghost" onClick={() => setEditProfile(false)}>
                    Abbrechen
                  </button>
                  <button className="vd-btn vd-btn--success" onClick={saveProfile}>
                    Speichern
                  </button>
                </div>
              )}
            </div>
            <div className="vd-profile-grid">
              {[
                ['name',            'Vereinsname'],
                ['email',           'E-Mail'],
                ['ansprechpartner', 'Ansprechpartner'],
                ['telefon',         'Telefon'],
                ['homepage',        'Homepage'],
                ['strasse',         'Straße'],
                ['hausnummer',      'Hausnummer'],
                ['plz',             'PLZ'],
                ['ort',             'Ort'],
              ].map(([field, label]) => (
                <div key={field} className="vd-profile-field">
                  <label className="vd-label">{label}</label>
                  {editProfile ? (
                    <input
                      className="vd-input"
                      value={profileForm[field] || ''}
                      onChange={e => setProfileForm({ ...profileForm, [field]: e.target.value })}
                    />
                  ) : (
                    <div className="vd-profile-value">{profile?.[field] || '–'}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            INTEGRATION — Dojosoftware API
        ══════════════════════════════════════════════════════════════════ */}
        {tab === 'integration' && (
          <div>
            {/* Status-Card */}
            <div className={`vd-card vd-integ-status ${integration ? 'vd-integ-status--connected' : 'vd-integ-status--none'}`}>
              <div className="vd-integ-status-icon">{integration ? '🔗' : '⚡'}</div>
              <div>
                <div className="vd-integ-status-title">
                  {integration ? 'Dojosoftware verbunden' : 'Noch nicht konfiguriert'}
                </div>
                {integration?.last_sync ? (
                  <div className="vd-integ-status-sub">
                    Letzter Sync: {fmt(integration.last_sync)}{' '}
                    {new Date(integration.last_sync).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr
                  </div>
                ) : integration ? (
                  <div className="vd-integ-status-sub">Noch kein Sync durchgeführt.</div>
                ) : (
                  <div className="vd-integ-status-sub">
                    Verbinden Sie Ihre Dojosoftware-Instanz, um Wettkämpfer automatisch zu importieren.
                  </div>
                )}
              </div>
              {integration && (
                <button
                  className="vd-btn vd-btn--gold"
                  onClick={syncNow}
                  disabled={integLoading}
                  style={{ marginLeft: 'auto' }}
                >
                  {integLoading ? 'Synchronisiert…' : '↻ Jetzt synchronisieren'}
                </button>
              )}
            </div>

            {/* Konfigurations-Form */}
            <div className="vd-card">
              <div className="vd-card-header">
                <h3>API-Konfiguration</h3>
              </div>
              <div className="vd-profile-grid">
                <div className="vd-form-field vd-form-field--full">
                  <label className="vd-label">Dojosoftware API-URL</label>
                  <input
                    className="vd-input"
                    placeholder="https://dojo.example.org/api"
                    value={integForm.dojo_api_url}
                    onChange={e => setIntegForm(f => ({ ...f, dojo_api_url: e.target.value }))}
                  />
                  <span className="vd-hint">
                    Basis-URL der Dojosoftware-API, z.B. <code>https://dojo.tda-intl.org/api</code>
                  </span>
                </div>
                <div className="vd-form-field vd-form-field--full">
                  <label className="vd-label">
                    API-Token{integration?.has_key ? ' (gespeichert — neuen Token eingeben um zu ändern)' : ''}
                  </label>
                  <input
                    className="vd-input"
                    type="password"
                    placeholder={integration?.has_key ? '••••••••••••••••' : 'API-Token eingeben'}
                    value={integForm.api_token}
                    onChange={e => setIntegForm(f => ({ ...f, api_token: e.target.value }))}
                    autoComplete="new-password"
                  />
                  <span className="vd-hint">
                    Den API-Token finden Sie in Ihren Dojosoftware-Einstellungen unter Integrationen.
                  </span>
                </div>
              </div>

              {/* Test-Ergebnis */}
              {integTestResult && (
                <div className={`vd-message vd-message--${integTestResult.ok ? 'success' : 'error'}`} style={{ margin: '0 0 16px' }}>
                  {integTestResult.msg}
                </div>
              )}

              <div className="vd-integ-actions">
                <button
                  className="vd-btn vd-btn--ghost"
                  onClick={testConnection}
                  disabled={integLoading || !integForm.dojo_api_url || !integForm.api_token}
                >
                  {integLoading ? 'Teste…' : '🔍 Verbindung testen'}
                </button>
                <button
                  className="vd-btn vd-btn--gold"
                  onClick={saveIntegration}
                  disabled={integLoading || !integForm.dojo_api_url || !integForm.api_token}
                >
                  {integLoading ? 'Speichert…' : '💾 Speichern'}
                </button>
              </div>
            </div>

            {/* Info-Box */}
            <div className="vd-card vd-integ-info">
              <h4>So funktioniert die Integration</h4>
              <ol>
                <li>Geben Sie die API-URL Ihrer Dojosoftware-Instanz ein.</li>
                <li>Tragen Sie den API-Token ein (zu finden in Dojosoftware → Einstellungen → Integrationen).</li>
                <li>Testen Sie die Verbindung und speichern Sie die Konfiguration.</li>
                <li>Klicken Sie auf <strong>Jetzt synchronisieren</strong> — Ihre Wettkämpfer werden automatisch importiert.</li>
              </ol>
              <p className="vd-hint" style={{ marginTop: 8 }}>
                Beim Sync werden bestehende Wettkämpfer (erkannt über die Dojo-ID) aktualisiert, neue werden hinzugefügt.
              </p>
            </div>
          </div>
        )}

      </div>{/* /vd-content */}

      {/* ══════════════════════════════════════════════════════════════════════
          MODAL: Wettkämpfer hinzufügen / bearbeiten
      ══════════════════════════════════════════════════════════════════════ */}
      {wkModal && (
        <div className="vd-modal-overlay" onClick={() => setWkModal(null)}>
          <div className="vd-modal" onClick={e => e.stopPropagation()}>
            <div className="vd-modal-header">
              <h3>{wkModal.type === 'add' ? 'Wettkämpfer hinzufügen' : 'Wettkämpfer bearbeiten'}</h3>
              <button className="vd-modal-close" onClick={() => setWkModal(null)}>✕</button>
            </div>
            <div className="vd-modal-body">
              <div className="vd-form-grid">
                {[['vorname', 'Vorname *'], ['nachname', 'Nachname *']].map(([f, l]) => (
                  <div key={f} className="vd-form-field">
                    <label className="vd-label">{l}</label>
                    <input
                      className="vd-input"
                      value={wkForm[f] || ''}
                      onChange={e => setWkForm({ ...wkForm, [f]: e.target.value })}
                    />
                  </div>
                ))}
                {/* Das Geburtsdatum ist serverseitig seit jeher Pflicht (5-99
                    Jahre) -- die Maske sagte es nur nicht. Wer es wegliess,
                    erfuhr das erst beim Speichern. */}
                <div className="vd-form-field">
                  <label className="vd-label" htmlFor="wk_geburtsdatum">
                    Geburtsdatum <span className="vd-pflicht" aria-hidden="true">*</span>
                  </label>
                  <input
                    id="wk_geburtsdatum"
                    type="date" required
                    className="vd-input"
                    value={wkForm.geburtsdatum ? wkForm.geburtsdatum.split('T')[0] : ''}
                    onChange={e => setWkForm({ ...wkForm, geburtsdatum: e.target.value })}
                  />
                  <small className="vd-feldhinweis">
                    Bestimmt die Altersklasse. Zwischen 5 und 99 Jahren.
                  </small>
                </div>
                <div className="vd-form-field">
                  <label className="vd-label">Geschlecht</label>
                  <select
                    className="vd-input"
                    value={wkForm.geschlecht || 'männlich'}
                    onChange={e => setWkForm({ ...wkForm, geschlecht: e.target.value })}
                  >
                    <option>männlich</option>
                    <option>weiblich</option>
                  </select>
                </div>
                {/* ⚠️ Pflichtfeld seit dem 16.09.2026. Ohne Gewicht filtert die
                    Anmeldemaske nicht nach Gewichtsklassen und bietet alle an --
                    bei der Einwaage faellt das auf, und die Gruppe muss neu
                    gebaut werden. Der Server weist es ohnehin ab; hier steht
                    es, damit niemand erst beim Speichern davon erfaehrt. */}
                <div className="vd-form-field">
                  <label className="vd-label" htmlFor="wk_gewicht">
                    Gewicht (kg) <span className="vd-pflicht" aria-hidden="true">*</span>
                  </label>
                  <input
                    id="wk_gewicht"
                    type="number" step="0.1" min="15" max="250" required
                    className="vd-input"
                    value={wkForm.gewicht || ''}
                    onChange={e => setWkForm({ ...wkForm, gewicht: e.target.value })}
                    placeholder="z. B. 65"
                  />
                  <small className="vd-feldhinweis">
                    Ohne Gewicht lässt sich keine Gewichtsklasse zuordnen.
                  </small>
                </div>
                <div className="vd-form-field">
                  <label className="vd-label">Gurtfarbe</label>
                  <select
                    className="vd-input"
                    value={wkForm.gurtfarbe || 'Weiß'}
                    onChange={e => setWkForm({
                      ...wkForm,
                      gurtfarbe: e.target.value,
                      skill_level: gurtToSkill(e.target.value),
                    })}
                  >
                    {GURTFARBEN.map(g => <option key={g}>{g}</option>)}
                  </select>
                </div>
                <div className="vd-form-field">
                  <label className="vd-label">Skill Level</label>
                  <select
                    className="vd-input"
                    value={wkForm.skill_level || 'Beginner'}
                    onChange={e => setWkForm({ ...wkForm, skill_level: e.target.value })}
                  >
                    {SKILL_LEVELS.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div className="vd-form-field">
                  <label className="vd-label">E-Mail</label>
                  <input
                    type="email"
                    className="vd-input"
                    value={wkForm.email || ''}
                    onChange={e => setWkForm({ ...wkForm, email: e.target.value })}
                  />
                </div>
              </div>
            </div>
            <div className="vd-modal-footer">
              <button className="vd-btn vd-btn--ghost" onClick={() => setWkModal(null)}>
                Abbrechen
              </button>
              <button
                className="vd-btn vd-btn--gold"
                onClick={saveWk}
                disabled={!wkForm.vorname || !wkForm.nachname}
              >
                {wkModal.type === 'add' ? 'Hinzufügen' : 'Speichern'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          MODAL: Turnier-Anmeldung
      ══════════════════════════════════════════════════════════════════════ */}
      {turnierModal && (
        <div className="vd-modal-overlay" onClick={() => setTurnierModal(null)}>
          <div className="vd-modal vd-modal--large" onClick={e => e.stopPropagation()}>
            <div className="vd-modal-header">
              <h3>Anmeldung: {turnierModal.turnier.name}</h3>
              <button className="vd-modal-close" onClick={() => setTurnierModal(null)}>✕</button>
            </div>
            <div className="vd-modal-body">
              <p className="vd-hint" style={{ marginBottom: 16 }}>
                {fmt(turnierModal.turnier.datum)}
                {turnierModal.turnier.ort ? ` · ${turnierModal.turnier.ort}` : ''}
              </p>
              <label className="vd-label" style={{ marginBottom: 8, display: 'block' }}>
                Wettkämpfer auswählen:
              </label>
              <div className="vd-athlete-list">
                {athletes.map(a => {
                  const id = a.wettkaempfer_id || a.id;
                  const checked = selectedAthletes.has(id);
                  return (
                    <label key={id} className={`vd-athlete-item ${checked ? 'checked' : ''}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleAthlete(id)}
                      />
                      <span className="vd-athlete-name">{a.nachname}, {a.vorname}</span>
                      <span className="vd-athlete-meta">
                        {a.gurtfarbe || '–'} · {a.geburtsdatum ? new Date(a.geburtsdatum).getFullYear() : '–'}
                      </span>
                    </label>
                  );
                })}
              </div>
              {selectedAthletes.size > 0 && (
                <div className="vd-modal-summary">
                  {selectedAthletes.size} Wettkämpfer ausgewählt
                </div>
              )}
              {/* Diese Abkuerzung meldet ohne Kategorie an. Ohne den Hinweis
                  hielt man die Anmeldung fuer fertig -- der Wettkaempfer stand
                  aber in keiner Division und damit in keiner Startliste. */}
              <div className="vd-modal-hinweis">
                {turnierModal.turnier.datum ? (
                  <>
                    Das ist eine <strong>Vormerkung ohne Kategorie</strong>. Die Divisionen
                    werden auf der Anmeldeseite gewählt — dort lässt sich die Vormerkung
                    jederzeit ergänzen.
                  </>
                ) : (
                  /* ⚠️ Ohne Datum gibt es keine Kategorien-Anmeldung
                     (Entscheidung des Veranstalters vom 16.09.2026). Der Knopf
                     „Mit Kategorien anmelden" fuehrte bis dahin trotzdem
                     dorthin -- und lief ins Leere, weil die Anmeldeseite
                     Turniere ohne Datum herausfiltert. Jetzt steht er gar
                     nicht erst da, und es steht dabei, warum. */
                  <>
                    Für dieses Turnier steht <strong>noch kein Datum</strong> fest. Es lässt
                    sich deshalb nur <strong>vormerken</strong> — Kategorien werden erst
                    gewählt, wenn der Termin steht. Die Vormerkung bleibt bestehen und
                    lässt sich dann ergänzen.
                  </>
                )}
              </div>
            </div>
            <div className="vd-modal-footer">
              <button className="vd-btn vd-btn--ghost" onClick={() => setTurnierModal(null)}>
                Abbrechen
              </button>
              {turnierModal.turnier.datum && (
                <button
                  className="vd-btn vd-btn--ghost"
                  onClick={() => {
                    const tid = turnierModal.turnier.turnier_id || turnierModal.turnier.id;
                    setTurnierModal(null);
                    navigate(`/registration?turnierId=${tid}`);
                  }}
                >
                  Mit Kategorien anmelden →
                </button>
              )}
              <button
                className="vd-btn vd-btn--gold"
                onClick={submitTurnierAnmeldung}
                disabled={selectedAthletes.size === 0}
              >
                {selectedAthletes.size > 0
                  ? `${selectedAthletes.size} vormerken`
                  : 'Vormerken'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
